import React from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Mail, User as UserIcon, Calendar, Check, X, ArrowRight } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function AdminAgenda() {
    const [mode, setMode] = React.useState("list");
    const [tasks, setTasks] = React.useState([]);
    const [contacts, setContacts] = React.useState([]);
    const [dateFrom, setDateFrom] = React.useState(today());
    const [dateTo, setDateTo] = React.useState(inDays(30));
    const [newTask, setNewTask] = React.useState({ title: "", description: "", date: today(), time: "", priority: "normal" });
    const [newContact, setNewContact] = React.useState({ name: "", phone: "", email: "", company: "", notes: "" });
    const [detail, setDetail] = React.useState(null);

    const loadTasks = React.useCallback(async () => {
        if (mode === "contacts") return;
        try {
            const r = await api.get(`/agenda/unified?date_from=${dateFrom}&date_to=${dateTo}`);
            const items = (r.data?.items || []).map(it => ({
                ...it,
                kind: it.kind,
                task_id: it.kind === "task" ? it.id : undefined,
                appointment_id: it.kind === "appointment" ? it.id : undefined,
                ticket_id: it.kind === "ticket" ? it.id : undefined,
            }));
            setTasks(items);
        } catch { /* fallback */
            const r = await api.get(`/agenda/tasks?date_from=${dateFrom}&date_to=${dateTo}`);
            setTasks(r.data || []);
        }
    }, [mode, dateFrom, dateTo]);
    const loadContacts = React.useCallback(async () => {
        const r = await api.get("/agenda/contacts");
        setContacts(r.data || []);
    }, []);

    React.useEffect(() => { if (mode === "tasks" || mode === "list" || mode === "calendar") loadTasks(); }, [mode, loadTasks]);
    React.useEffect(() => { if (mode === "contacts") loadContacts(); }, [mode, loadContacts]);

    const addTask = async (e) => {
        e.preventDefault();
        if (!newTask.title.trim()) return toast.error("Titlu obligatoriu");
        await api.post("/agenda/tasks", newTask);
        setNewTask({ title: "", description: "", date: today(), time: "", priority: "normal" });
        loadTasks(); toast.success("Task adăugat");
    };
    const toggleDone = async (t) => {
        await api.put(`/agenda/tasks/${t.task_id}`, { done: !t.done });
        loadTasks();
    };
    const delTask = async (id) => {
        if (!window.confirm("Ștergi?")) return;
        await api.delete(`/agenda/tasks/${id}`); loadTasks();
    };
    const addContact = async (e) => {
        e.preventDefault();
        if (!newContact.name.trim()) return toast.error("Nume obligatoriu");
        await api.post("/agenda/contacts", newContact);
        setNewContact({ name: "", phone: "", email: "", company: "", notes: "" });
        loadContacts(); toast.success("Contact salvat");
    };
    const delContact = async (id) => {
        if (!window.confirm("Ștergi?")) return;
        await api.delete(`/agenda/contacts/${id}`); loadContacts();
    };

    const byDate = tasks.reduce((acc, t) => { (acc[t.date || "Fără dată"] ||= []).push(t); return acc; }, {});

    // Calendar grid (month view from dateFrom)
    const calendarDays = React.useMemo(() => {
        if (mode !== "calendar") return [];
        const start = new Date(dateFrom);
        const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
        const gridStart = new Date(monthStart);
        const dow = (monthStart.getDay() + 6) % 7; // Mon=0
        gridStart.setDate(gridStart.getDate() - dow);
        const days = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            const iso = d.toISOString().slice(0, 10);
            days.push({ date: iso, items: byDate[iso] || [],
                        outMonth: d.getMonth() !== monthStart.getMonth(),
                        isToday: iso === today() });
        }
        return days;
    }, [mode, dateFrom, byDate]);

    return (
        <div data-testid="admin-agenda">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-2">Agenda & CRM</h1>
            <p className="text-sm text-muted-foreground mb-6">Programări personale, lead-uri și contacte comerciale.</p>

            <div className="flex gap-1 mb-6 border-b border-border">
                <button onClick={() => setMode("list")} className={`px-4 py-2 text-sm font-medium ${mode === "list" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="agenda-tab-list">Listă</button>
                <button onClick={() => setMode("calendar")} className={`px-4 py-2 text-sm font-medium ${mode === "calendar" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="agenda-tab-calendar">Calendar</button>
                <button onClick={() => setMode("contacts")} className={`px-4 py-2 text-sm font-medium ${mode === "contacts" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="agenda-tab-contacts">Contacte</button>
            </div>

            {mode === "calendar" && (
                <div data-testid="agenda-calendar">
                    <div className="grid grid-cols-7 gap-1 mb-1 text-xs text-muted-foreground uppercase">
                        {["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"].map(d => <div key={d} className="px-2">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day, i) => (
                            <div key={i} className={`min-h-[100px] p-2 rounded-lg border ${day.outMonth ? "bg-muted/30 border-transparent" : "bg-card border-border"} ${day.isToday ? "ring-2 ring-primary" : ""}`}>
                                <div className={`text-xs font-medium mb-1 ${day.outMonth ? "text-muted-foreground/50" : ""}`}>{day.date.slice(8, 10)}</div>
                                <div className="space-y-1">
                                    {day.items.slice(0, 3).map(it => (
                                        <button key={(it.task_id || it.appointment_id || it.ticket_id || it.id)}
                                             onClick={() => setDetail(it)}
                                             className={`block w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate hover:opacity-80 transition-opacity ${
                                                 it.kind === "appointment" ? "bg-primary/10 text-primary" :
                                                 it.kind === "ticket" ? "bg-amber-100 text-amber-900" :
                                                 it.done ? "bg-muted text-muted-foreground line-through" :
                                                 "bg-foreground/10 text-foreground"
                                             }`} title={it.title || it.description}
                                             data-testid={`cal-item-${it.id}`}>
                                            {(it.time || "") + " " + (it.title || "")}
                                        </button>
                                    ))}
                                    {day.items.length > 3 && <div className="text-[10px] text-muted-foreground">+{day.items.length - 3}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {mode === "list" && (
                <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
                    <form onSubmit={addTask} className="card-soft p-5 space-y-3 h-fit">
                        <h3 className="font-heading text-lg flex items-center gap-2"><Plus className="h-4 w-4" /> Task nou</h3>
                        <input required placeholder="Titlu (ex: Sună client X)" value={newTask.title} onChange={(e) => setNewTask(t => ({ ...t, title: e.target.value }))} className="input-soft" data-testid="agenda-task-title" />
                        <textarea rows="2" placeholder="Detalii..." value={newTask.description} onChange={(e) => setNewTask(t => ({ ...t, description: e.target.value }))} className="input-soft" />
                        <div className="grid grid-cols-2 gap-2">
                            <input type="date" value={newTask.date} onChange={(e) => setNewTask(t => ({ ...t, date: e.target.value }))} className="input-soft" />
                            <input type="time" value={newTask.time} onChange={(e) => setNewTask(t => ({ ...t, time: e.target.value }))} className="input-soft" />
                        </div>
                        <select value={newTask.priority} onChange={(e) => setNewTask(t => ({ ...t, priority: e.target.value }))} className="input-soft">
                            <option value="low">Prioritate scăzută</option>
                            <option value="normal">Prioritate normală</option>
                            <option value="high">Prioritate ridicată</option>
                            <option value="urgent">Urgent</option>
                        </select>
                        <button type="submit" className="btn-primary w-full" data-testid="agenda-task-add">Adaugă</button>
                    </form>
                    <div>
                        <div className="card-soft p-3 mb-4 flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Interval:</span>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-soft !py-1 !text-xs" />
                            <span className="text-muted-foreground">→</span>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-soft !py-1 !text-xs" />
                        </div>
                        <div className="space-y-4">
                            {Object.keys(byDate).sort().map(d => (
                                <div key={d}>
                                    <div className="text-xs text-muted-foreground font-medium mb-2 uppercase tracking-wide">{d}</div>
                                    <div className="space-y-2">
                                        {byDate[d].map(t => (
                                            <button key={t.task_id || t.appointment_id || t.ticket_id || t.id}
                                                    onClick={() => setDetail(t)}
                                                    className={`card-soft p-3 flex items-center gap-3 w-full text-left hover:border-primary transition-colors ${t.done ? "opacity-60" : ""}`}
                                                    data-testid={`agenda-item-${t.task_id || t.appointment_id || t.ticket_id || t.id}`}>
                                                {t.kind === "task" && (
                                                    <span onClick={(e) => { e.stopPropagation(); toggleDone(t); }}
                                                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${t.done ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                                                        {t.done && <Check className="h-4 w-4" />}
                                                    </span>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className={`font-medium text-sm truncate ${t.done ? "line-through" : ""}`}>{t.title}</div>
                                                    {t.time && <div className="text-xs text-muted-foreground">{t.time}</div>}
                                                    {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
                                                </div>
                                                {t.kind === "appointment" && <div className="chip chip-primary text-[10px]">Instalare</div>}
                                                {t.kind === "ticket" && <div className="chip text-[10px] bg-amber-100 text-amber-900">Ticket</div>}
                                                {t.priority === "urgent" && <div className="chip text-[10px] bg-destructive text-destructive-foreground">Urgent</div>}
                                                {t.priority === "high" && <div className="chip text-[10px] bg-primary text-primary-foreground">Ridicat</div>}
                                                {t.kind === "task" && <span onClick={(e) => { e.stopPropagation(); delTask(t.task_id); }} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></span>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {Object.keys(byDate).length === 0 && <p className="text-muted-foreground">Nicio activitate programată în intervalul selectat.</p>}
                        </div>
                    </div>
                </div>
            )}

            {mode === "contacts" && (
                <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
                    <form onSubmit={addContact} className="card-soft p-5 space-y-3 h-fit">
                        <h3 className="font-heading text-lg">Contact / Lead nou</h3>
                        <input required placeholder="Nume" value={newContact.name} onChange={(e) => setNewContact(c => ({ ...c, name: e.target.value }))} className="input-soft" data-testid="agenda-contact-name" />
                        <input placeholder="Telefon" value={newContact.phone} onChange={(e) => setNewContact(c => ({ ...c, phone: e.target.value }))} className="input-soft" />
                        <input type="email" placeholder="Email" value={newContact.email} onChange={(e) => setNewContact(c => ({ ...c, email: e.target.value }))} className="input-soft" />
                        <input placeholder="Companie" value={newContact.company} onChange={(e) => setNewContact(c => ({ ...c, company: e.target.value }))} className="input-soft" />
                        <textarea rows="3" placeholder="Note (buget, interese, urmărire)" value={newContact.notes} onChange={(e) => setNewContact(c => ({ ...c, notes: e.target.value }))} className="input-soft" />
                        <button type="submit" className="btn-primary w-full" data-testid="agenda-contact-add">Salvează</button>
                    </form>
                    <div className="space-y-2">
                        {contacts.length === 0 && <p className="text-muted-foreground">Niciun contact salvat încă.</p>}
                        {contacts.map(c => (
                            <div key={c.contact_id} className="card-soft p-4" data-testid={`agenda-contact-${c.contact_id}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-medium flex items-center gap-2"><UserIcon className="h-4 w-4 text-muted-foreground" /> {c.name} {c.company && <span className="text-muted-foreground text-sm">· {c.company}</span>}</div>
                                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-1">
                                            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-primary"><Phone className="h-3 w-3" /> {c.phone}</a>}
                                            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-primary"><Mail className="h-3 w-3" /> {c.email}</a>}
                                        </div>
                                        {c.notes && <div className="text-sm mt-2">{c.notes}</div>}
                                    </div>
                                    <button onClick={() => delContact(c.contact_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {detail && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4" data-testid="agenda-detail-modal">
                    <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                    {detail.kind === "task" ? "Task personal" : detail.kind === "appointment" ? "Programare" : "Ticket"}
                                </div>
                                <h2 className="font-heading text-xl">{detail.title}</h2>
                            </div>
                            <button onClick={() => setDetail(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="space-y-2 text-sm">
                            {detail.date && <div><span className="text-muted-foreground">Data:</span> {detail.date}{detail.time ? ` · ${detail.time}` : ""}</div>}
                            {detail.description && <div><span className="text-muted-foreground">Descriere:</span> <span className="whitespace-pre-wrap">{detail.description}</span></div>}
                            {detail.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><a href={`tel:${detail.phone}`} className="text-primary hover:underline">{detail.phone}</a></div>}
                            {detail.name && detail.kind !== "task" && <div><span className="text-muted-foreground">Client:</span> {detail.name}</div>}
                            {detail.address && <div><span className="text-muted-foreground">Adresa:</span> {detail.address}</div>}
                            {detail.status && <div><span className="text-muted-foreground">Status:</span> <span className="chip text-[10px] bg-muted">{detail.status}</span></div>}
                            {detail.priority && detail.priority !== "normal" && <div><span className="text-muted-foreground">Prioritate:</span> {detail.priority}</div>}
                            {detail.notes && <div><span className="text-muted-foreground">Note:</span> <span className="whitespace-pre-wrap">{detail.notes}</span></div>}
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            {detail.kind === "appointment" && (
                                <Link to="/admin/appointments" onClick={() => setDetail(null)} className="btn-outline text-sm flex items-center gap-1" data-testid="agenda-detail-goto">
                                    Deschide în Programări <ArrowRight className="h-3 w-3" />
                                </Link>
                            )}
                            {detail.kind === "ticket" && (
                                <Link to={`/admin/tickets?id=${detail.id}`} onClick={() => setDetail(null)} className="btn-outline text-sm flex items-center gap-1" data-testid="agenda-detail-ticket">
                                    Deschide ticket <ArrowRight className="h-3 w-3" />
                                </Link>
                            )}
                            {detail.kind === "task" && (
                                <button onClick={() => { toggleDone(detail); setDetail(null); }}
                                        className="btn-primary text-sm flex items-center gap-1">
                                    <Check className="h-3 w-3" /> {detail.done ? "Marchează ne-rezolvat" : "Marchează rezolvat"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
